import React, { useState, useEffect } from "react";
import { useGetIndividualMovementHistoryQuery } from "../../redux/slices/apiSlice";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ColorRing } from "react-loader-spinner";
import CustomTooltip from "../HigherLevelAnalysis/CustomTooltip";
import { skipToken } from "@reduxjs/toolkit/query";

const MIHigherLevelIndividualMoment = ({
  accountDetails,
  selectedShape,
  selectedCaratRange,
  selectedNeedsMetric,
  selectedLocation
  
}) => {
  // Tab configuration with display options and their corresponding API values
  const optionsConfig = {
    "Shape": {
      prop_name: "shape",
      options: [
        { display: "Round", value: "0000round" },
        { display: "Princess", value: "0001princess" },
        { display: "Pear", value: "0002pear" },
        { display: "Oval", value: "0003oval" },
        { display: "Emerald", value: "0004emerald" },
        { display: "Marquise", value: "0005marquise" },
        { display: "Cushion", value: "0007cushion_brilliant" },
        { display: "Radiant", value: "0009radiant" },
        { display: "Asscher", value: "0011asscher" }
      ]
    },
    "Carat Range": {
      prop_name: "carats",
      options: [
        { display: "10.0-99.99", value: "10.0-99.99" },
        { display: "5.0-5.99", value: "5.0-5.99" },
        { display: "4.0-4.99", value: "4.0-4.99" },
        { display: "3.0-3.99", value: "3.0-3.99" },
        { display: "2.0-2.99", value: "2.0-2.99" },
        { display: "1.5-1.99", value: "1.5-1.99" },
        { display: "1.0-1.49", value: "1.0-1.49" },
        { display: "0.9-0.99", value: "0.9-0.99" },
        { display: "0.7-0.89", value: "0.7-0.89" },
        { display: "0.5-0.69", value: "0.5-0.69" },
        { display: "0.4-0.49", value: "0.4-0.49" },
        { display: "0.3-0.39", value: "0.3-0.39" },
        { display: "0.2-0.29", value: "0.2-0.29" },
        { display: "0-0.19", value: "0-0.19" },
      ]
    }
  };

  // State for active filter (which one is being used for the query)
  const [activeFilter, setActiveFilter] = useState("Shape");

  // State for API parameters
  const [queryParams, setQueryParams] = useState({
    id: accountDetails.account_id,
    shape: selectedShape,
    carat_range: selectedCaratRange,
    location: selectedLocation,
    duration_in_weeks: selectedNeedsMetric,

  });
  const isReady =
  accountDetails?.account_id &&
  selectedShape &&
  selectedCaratRange &&
  selectedLocation &&
  selectedNeedsMetric;

  // Track the current and previous query params to detect changes
  const [currentQueryId, setCurrentQueryId] = useState("initial");
  const [isChangingParams, setIsChangingParams] = useState(false);
  const queryArgs = isReady
  ? {
      id: accountDetails.account_id,
      shape: selectedShape,
      carat_range: selectedCaratRange,
      location: selectedLocation,
      duration_in_weeks: selectedNeedsMetric,
    }
  : skipToken;
  // Fetch data using current query parameters
  const { data, isLoading, isFetching } = useGetIndividualMovementHistoryQuery(queryArgs, {
    refetchOnMountOrArgChange: true
  });

  // Find the corresponding values for the selected shape and carat range from parent
  useEffect(() => {
    let shapeValue;
    let caratValue;
    
    // Find the shape value
    const shapeOption = optionsConfig["Shape"].options.find(opt => 
      opt.display.toLowerCase() === selectedShape.split('0')[1] || // Extract display name from shape code
      opt.value === selectedShape
    );
    
    if (shapeOption) {
      shapeValue = shapeOption.value;
    }
    
    // Find the carat value
    const caratOption = optionsConfig["Carat Range"].options.find(opt => 
      opt.display === selectedCaratRange || 
      opt.value === selectedCaratRange
    );
    
    if (caratOption) {
      caratValue = caratOption.value;
    }
    
    // Update query parameters based on active filter
    const activeConfig = optionsConfig[activeFilter];
    const propValue = activeFilter === "Shape" ? shapeValue : caratValue;
    
    // Only update if we have a valid value
    if (propValue) {
      setIsChangingParams(true);
      const newQueryId = `${activeConfig.prop_name}-${propValue}-${Date.now()}`;
      setCurrentQueryId(newQueryId);
      setQueryParams({
        id: accountDetails.account_id,
        shape: selectedShape,
        carat_range: selectedCaratRange,
        location: selectedLocation,
        duration_in_weeks: selectedNeedsMetric,
      });
    }
  }, [activeFilter, selectedShape, selectedCaratRange, selectedLocation, selectedNeedsMetric]);

  // Reset the changing flag when data is loaded
  useEffect(() => {
    if (!isLoading && !isFetching && isChangingParams) {
      setIsChangingParams(false);
    }
  }, [isLoading, isFetching]);

  // Handle filter change (Shape or Carat Range)
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  // Process chart data from API response
  const chartData = data?.data
    ? Object.entries(data.data.history_data).map(([week, values]) => ({
        week,
        price_change: values.price_change,
        overhang: values.overhang,
        min_ppc: values.min_ppc,
        max_ppc: values.max_ppc,
        price_change_10th: values.price_change_10th,
        price_change_90th: values.price_change_90th,
      }))
    : [];

  const [showMin, setShowMin] = useState(false);
  const [showMax, setShowMax] = useState(false);

  const toggleMinLine = () => {
    setShowMin(prevState => !prevState);
  };

  const toggleMaxLine = () => {
    setShowMax(prevState => !prevState);
  };

  // Determine if we should show the loading state
  const showLoader = isLoading || isFetching || isChangingParams;

  return (
    <div className="flex flex-col w-[755px] bg-white  rounded-lg px-2 py-4">
      {/* Title and Filter Display */}
      

      {/* Chart Section */}
      <div className="w-full h-96">
        {showLoader ? (
          <div className="flex justify-center w-full items-center h-full">
            <ColorRing
              visible={true}
              height="60"
              width="60"
              ariaLabel="blocks-loading"
              colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
            />
          </div>
        ) : chartData.length > 0 ? (
          <div className={`w-full h-full  transition-opacity duration-500 ${showLoader ? 'opacity-50' : 'opacity-100'}`}>
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 30, right: 0, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 13 }}
                  tickFormatter={(value) => {
                    const [year, month, day] = value.split("-");
                    return `${month}/${day}/${year.slice(2)}`;
                  }}
                />
                <YAxis
                  yAxisId="left"
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 13 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `${value}`}
                  tick={{ fontSize: 13 }}
                />
                <Tooltip
                  formatter={(value) => `${value}`}
                  content={<CustomTooltip />}
                  labelStyle={{ fontWeight: 'bold' }}
                />
                {/* <Line
                  yAxisId="left"
                  type="basis"
                  dataKey="price_change_10th"
                  stroke="#8884d8"
                  strokeDasharray="4 4"
                  strokeWidth={2.5}
                  dot={false}
                  name="Min"
                  hide={!showMin}
                  isAnimationActive={true}
                  animationEasing="ease-in-out"
                  animationBegin={2000}
                  animationDuration={5000}
                />
                <Line
                  yAxisId="left"
                  type="basis"
                  dataKey="price_change_90th"
                  stroke="#82ca9d"
                  strokeDasharray="4 4"
                  strokeWidth={2.5}
                  dot={false}
                  name="Max"
                  hide={!showMax}
                  isAnimationActive={true}
                /> */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="price_change"
                  stroke="#1f2d3d"
                  strokeWidth={2.5}
                  dot={false}
                  name="Price Change"
                  isAnimationActive={true}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="overhang"
                  stroke="#65b0ac"
                  strokeWidth={2.5}
                  dot={false}
                  name="Inventory Overhang"
                  isAnimationActive={true}
                />
                <Legend verticalAlign="bottom" height={30} />
              </LineChart>
            </ResponsiveContainer>
            
            {/* <div className="flex items-center justify-center gap-6 mt-2"> */}
              {/* Min checkbox */}
              {/* <div className="flex items-center gap-1">
                <label htmlFor="showMinCheckbox" className="flex items-center gap-2 cursor-pointer">
                  <span
                    style={{
                      width: "12px",
                      height: "2px",
                      backgroundColor: "#8884d8",
                      display: "inline-block",
                    }}
                  ></span>
                  <span>Min</span>
                </label>
                <input
                  className="cursor-pointer w-3 h-3 z-99 relative"
                  type="checkbox"
                  id="showMinCheckbox"
                  checked={showMin}
                  onChange={toggleMinLine}
                />
              </div> */}
              
              {/* Max checkbox */}
              {/* <div className="flex items-center gap-1">
                <label htmlFor="showMaxCheckbox" className="flex items-center gap-2 ">
                  <span
                    style={{
                      width: "12px",
                      height: "2px",
                      backgroundColor: "#82ca9d",
                      display: "inline-block",
                    }}
                  ></span>
                  <span>Max</span>
                </label>
                <input
                  className="cursor-pointer w-3 h-3 z-99 relative"
                  type="checkbox"
                  id="showMaxCheckbox"
                  checked={showMax}
                  onChange={toggleMaxLine}
                />
              </div> */}
            {/* </div> */}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p>Market Information not available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MIHigherLevelIndividualMoment;